import { useEffect, useRef, useState } from "react";
import { Plus, Trash2, Save, ArrowLeft, ExternalLink, ImagePlus, Star } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import ImageUploader from "@/components/admin/ImageUploader";
import Markdown from "@/components/store/Markdown";
import { mediaUrl } from "@/lib/api";
import { toast } from "sonner";

const BLANK = {
  title: "", slug: "", excerpt: "", content: "", cover_image: "", cover_alt: "",
  tags: [], keywords: "", author: "The Sew Voyage Atelier", status: "draft",
  featured: false, meta_title: "", meta_description: "", og_image: "",
};

export default function AdminBlog() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(BLANK);
  const [tagsInput, setTagsInput] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/admin/blog/posts").then(({ data }) => setList(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setF(BLANK); setTagsInput(""); setEditing("new"); };
  const openEdit = async (p) => {
    const { data } = await api.get(`/admin/blog/posts/${p.id}`);
    setF({ ...BLANK, ...data }); setTagsInput((data.tags || []).join(", ")); setEditing(data.id);
  };
  const set = (k, v) => setF((d) => ({ ...d, [k]: v }));

  const save = async () => {
    if (!f.title) return toast.error("A title is required.");
    const payload = { ...f, tags: tagsInput.split(",").map((t) => t.trim()).filter(Boolean) };
    try {
      if (editing === "new") await api.post("/admin/blog/posts", payload);
      else await api.put(`/admin/blog/posts/${editing}`, payload);
      toast.success("Post saved."); setEditing(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (p) => { if (!window.confirm(`Delete "${p.title}"?`)) return; await api.delete(`/admin/blog/posts/${p.id}`); load(); };

  const insertImage = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("content", `${f.content}\n\n![Describe this image for SEO](${data.url})\n\n`);
      toast.success("Image inserted — add alt text in the brackets.");
    } catch { toast.error("Upload failed."); }
    setUploading(false); e.target.value = "";
  };

  if (editing) {
    return (
      <div data-testid="admin-blog-editor">
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => setEditing(null)} className="inline-flex items-center gap-2 font-body uppercase tracking-wide2 text-[0.6rem] text-ink/50 hover:text-ink" data-testid="blog-back"><ArrowLeft size={13} /> All Posts</button>
          <div className="flex items-center gap-3">
            {editing !== "new" && f.status === "published" && <a href={`${window.location.origin}/journal/${f.slug}`} target="_blank" rel="noreferrer" className="adm-btn-outline" data-testid="blog-preview"><ExternalLink size={13} /> View</a>}
            <button onClick={save} className="adm-btn" data-testid="blog-save"><Save size={14} /> Save Post</button>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          <div className="space-y-5">
            <div><label className="adm-label">Title</label><input className="adm-input" value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="blog-title" placeholder="An evocative headline" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="adm-label">Slug (URL)</label><input className="adm-input" value={f.slug} onChange={(e) => set("slug", e.target.value)} data-testid="blog-slug" placeholder="auto from title" /></div>
              <div><label className="adm-label">Author</label><input className="adm-input" value={f.author} onChange={(e) => set("author", e.target.value)} data-testid="blog-author" /></div>
            </div>
            <div><label className="adm-label">Excerpt (card + meta preview)</label><textarea className="adm-input" rows={2} value={f.excerpt} onChange={(e) => set("excerpt", e.target.value)} data-testid="blog-excerpt" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="adm-label">Tags (comma separated)</label><input className="adm-input" value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} data-testid="blog-tags" placeholder="Styling, Travel" /></div>
              <div><label className="adm-label">SEO Keywords</label><input className="adm-input" value={f.keywords} onChange={(e) => set("keywords", e.target.value)} data-testid="blog-keywords" placeholder="resort wear, linen, sustainable" /></div>
            </div>
            <div className="flex items-center gap-6">
              <div className="flex-1"><label className="adm-label">Status</label>
                <select className="adm-input" value={f.status} onChange={(e) => set("status", e.target.value)} data-testid="blog-status"><option value="draft">Draft</option><option value="published">Published</option></select>
              </div>
              <label className="flex items-center gap-2 font-body text-sm mt-5"><input type="checkbox" checked={!!f.featured} onChange={(e) => set("featured", e.target.checked)} className="accent-ink w-4 h-4" data-testid="blog-featured" /><Star size={14} /> Featured</label>
            </div>

            <ImageUploader testId="blog-cover" label="Cover Image" recommended="1600×900 · 16:9" value={f.cover_image} onChange={(v) => set("cover_image", v)} />
            <div><label className="adm-label">Cover Alt Text (SEO & accessibility)</label><input className="adm-input" value={f.cover_alt} onChange={(e) => set("cover_alt", e.target.value)} data-testid="blog-cover-alt" placeholder="Describe the cover image" /></div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="adm-label mb-0">Content (Markdown)</label>
                <button type="button" onClick={() => fileRef.current?.click()} className="adm-btn-outline text-[0.6rem]" data-testid="blog-insert-image"><ImagePlus size={12} /> {uploading ? "Uploading…" : "Insert Image"}</button>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={insertImage} data-testid="blog-insert-image-file" />
              </div>
              <textarea className="adm-input font-mono text-xs leading-relaxed" rows={18} value={f.content} onChange={(e) => set("content", e.target.value)} data-testid="blog-content" placeholder="## A heading&#10;&#10;Your story… Use ## for sections, > for quotes, ![alt](url) for images." />
              <p className="font-body text-[0.6rem] text-ink/40 mt-1">## Heading · **bold** · *italic* · &gt; quote · - list · [text](url) · ![alt](image-url)</p>
            </div>

            <div className="border-t border-ink/10 pt-5 space-y-4">
              <p className="adm-label">Search & Social (SEO)</p>
              <div><label className="adm-label">Meta Title</label><input className="adm-input" value={f.meta_title} onChange={(e) => set("meta_title", e.target.value)} data-testid="blog-meta-title" placeholder="Defaults to post title" /></div>
              <div><label className="adm-label">Meta Description</label><textarea className="adm-input" rows={2} value={f.meta_description} onChange={(e) => set("meta_description", e.target.value)} data-testid="blog-meta-desc" placeholder="Defaults to excerpt" /></div>
              <ImageUploader testId="blog-og" label="Social Share Image (Open Graph)" recommended="1200×630 · defaults to cover" value={f.og_image} onChange={(v) => set("og_image", v)} />
            </div>
          </div>

          <div className="lg:sticky lg:top-6 self-start">
            <p className="adm-label mb-3">Live Preview</p>
            <div className="border border-ink/10 bg-linen p-6 max-h-[80vh] overflow-y-auto" data-testid="blog-preview-pane">
              {f.cover_image && <div className="overflow-hidden bg-cream aspect-[16/9] mb-6"><img src={mediaUrl(f.cover_image)} alt={f.cover_alt} className="w-full h-full object-cover vintage-photo" /></div>}
              <p className="eyebrow mb-2">{tagsInput.split(",")[0]}</p>
              <h1 className="font-heading text-3xl tracking-tight mb-4">{f.title || "Untitled"}</h1>
              <Markdown content={f.content} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-blog">
      <div className="flex items-center justify-between mb-8">
        <div><h1 className="font-heading text-3xl">Blog / Journal</h1><p className="font-body text-sm text-ink/50 mt-1">Write, publish and manage your stories.</p></div>
        <button onClick={openNew} className="adm-btn" data-testid="blog-new"><Plus size={14} /> New Post</button>
      </div>

      <div className="adm-card p-0 overflow-hidden">
        <table className="w-full font-body text-sm">
          <thead><tr className="text-left uppercase tracking-wide2 text-[0.55rem] text-ink/50 border-b border-ink/10"><th className="px-5 py-3">Title</th><th className="px-5 py-3">Tags</th><th className="px-5 py-3">Status</th><th className="px-5 py-3 text-right">Views</th><th className="px-5 py-3"></th></tr></thead>
          <tbody>
            {list.map((p) => (
              <tr key={p.id} className="border-b border-ink/5 hover:bg-warm/20 cursor-pointer" data-testid={`blog-row-${p.slug}`} onClick={() => openEdit(p)}>
                <td className="px-5 py-3 flex items-center gap-2">{p.featured && <Star size={12} className="text-ink" />}<span className="font-heading text-base">{p.title}</span></td>
                <td className="px-5 py-3 text-ink/60">{(p.tags || []).join(", ")}</td>
                <td className="px-5 py-3"><span className={`px-2 py-0.5 text-[0.6rem] uppercase tracking-wide2 ${p.status === "published" ? "bg-ink text-linen" : "bg-warm text-ink/70"}`}>{p.status}</span></td>
                <td className="px-5 py-3 text-right text-ink/60">{p.views || 0}</td>
                <td className="px-5 py-3 text-right"><button onClick={(e) => { e.stopPropagation(); del(p); }} className="text-destructive hover:opacity-70" data-testid={`blog-delete-${p.slug}`}><Trash2 size={14} /></button></td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={5} className="px-5 py-12 text-center text-ink/40">No posts yet. Write your first story.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
